import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react';
import { useAppContext, useLogout } from '@frx/frx-react-context';
import cn from 'classnames';
import { standardizeUrl } from '@frx/frx-services';
import { HeaderLogo } from './HeaderLogo';
import { HeaderMenuLinks } from './HeaderMenuLinks';
import { HeaderMenuToggle } from './HeaderMenuToggle';
import { HeaderMenuItem } from './HeaderMenuItem';
import { useIsMobile } from '../../../hooks';

import {
  ChangeEvent,
  ChevronDown,
  Login,
  Logout,
  QuestionMark,
} from '../../../assets';
import { SideNavItem, UserMenu } from './UserMenu';
import { UserAvatar } from '../../user-avatar';

import styles from './Header.module.scss';
import { getDataTracking } from '../../../utils';
import { setEvent } from '../../../analytics';

export const Header = () => {
  const {
    data: {
      authData: { loggedIn, user },
    },
  } = useAppContext();
  const { mutate: logout } = useLogout();
  const location = useLocation();
  const isMobile = useIsMobile();
  const navigateTo = useNavigate();

  // Handle mobile menu closing on link click
  const [isOpen, setOpen] = useState(false);
  useEffect(() => setOpen(false), [location.key]);
  const onToggle = () => setOpen((prevState) => !prevState);

  const useFrxAuthentication =
    process.env.REACT_APP_USE_FRX_AUTHENTICATION === 'true';

  const handleLogout = () => {
    if (useFrxAuthentication) {
      // TODO: Add frid to session to be captured in session_logout
      const redirectUrl = standardizeUrl(
        `/site/SPageServer/?pagename=session_logout`
      );
      window.location.href = standardizeUrl(
        `site/UserLogin?logout=true&NEXTURL=${redirectUrl}`
      );
    } else {
      logout(undefined, {
        onSuccess: () => {
          setEvent({ pathname: '/logout' });
          navigateTo('/login');
        },
      });
    }
  };

  const isRegistered = user?.isRegistered;
  const changeEvent = standardizeUrl(
    '/site/SPageServer/?pagename=all_events_center'
  );

  return (
    <Flex
      as="nav"
      align="center"
      justify="space-between"
      wrap="wrap"
      w="100%"
      bg={['white']}
      boxShadow="base"
    >
      <Box p={4}>
        <HeaderLogo />
      </Box>

      <HeaderMenuToggle isOpen={isOpen} toggle={onToggle} />

      <HeaderMenuLinks isOpen={isOpen}>
        <HeaderMenuItem to="contactus" icon={QuestionMark}>
          Contact Us
        </HeaderMenuItem>

        {isMobile && loggedIn && (
          <HeaderMenuItem href={changeEvent} icon={ChangeEvent}>
            Change event
          </HeaderMenuItem>
        )}

        <Divider
          h={[0, 0, 6]}
          borderColor="brand.monochromatic.200"
          orientation={isMobile ? 'horizontal' : 'vertical'}
        />

        {loggedIn ? (
          isMobile ? (
            <>
              <HStack>
                <UserAvatar
                  size="sm"
                  src={user?.avatar}
                  name={`${user?.name?.first} ${user?.name?.last}`}
                  aria-label={`${user?.cons_id} Avatar - Header`}
                />
                <Text
                  as="h3"
                  textStyle="h3"
                  color="brand.monochromatic.900"
                  fontWeight="extrabold"
                >
                  {user?.name?.first} {user?.name?.last}
                </Text>
              </HStack>
              {isRegistered && <UserMenu />}
              <SideNavItem
                icon={Logout}
                label="Logout"
                onClick={handleLogout}
              />
            </>
          ) : (
            <Menu offset={[0, 22]}>
              <MenuButton
                {...getDataTracking(`${user?.user_name}-header-profile`)}
                as={Button}
                variant="ghost"
                rightIcon={
                  <ChevronDown boxSize="18px" color="brand.primary.darkGrey" />
                }
                style={{ marginLeft: 0 }}
                aria-label={`${user?.name?.first} ${user?.name?.last} menuToggle`}
              >
                <HStack spacing={2}>
                  <UserAvatar
                    src={user?.avatar}
                    name={`${user?.name?.first} ${user?.name?.last}`}
                    aria-label={`${user?.cons_id} Avatar - Header`}
                  />
                  <Text as="h5" textStyle="h5" color="brand.primary.darkGrey">
                    {user?.name?.first} {user?.name?.last}
                  </Text>
                </HStack>
              </MenuButton>
              <MenuList
                aria-label="menuDropdown"
                bg="brand.monochromatic.50"
                minWidth="320px"
                borderRadius="none"
              >
                <MenuItem
                  as="a"
                  href={changeEvent}
                  icon={<ChangeEvent boxSize="24px" />}
                  onClick={handleLogout}
                  className={cn(styles.headerDropdownLink)}
                  {...getDataTracking('headerChangeEventLink')}
                >
                  Change event
                </MenuItem>
                <MenuItem
                  icon={<Logout boxSize="24px" />}
                  onClick={handleLogout}
                  className={cn(styles.headerDropdownLink)}
                  {...getDataTracking('headerLogoutLink')}
                >
                  Logout
                </MenuItem>
              </MenuList>
            </Menu>
          )
        ) : (
          <HeaderMenuItem to="login" icon={Login}>
            Log In
          </HeaderMenuItem>
        )}
      </HeaderMenuLinks>
    </Flex>
  );
};
